import {
  accountListColumnsKey,
  assistantThreadKey,
  clientHubSectionsKey,
  clientListHeadingsKey,
  inboxFilterKey,
  lastCreateProjectKey,
  lastRouteKey,
  lastWorkspaceKey,
  modeRouteKey,
  onboardingProgressKey,
  recentItemsKey,
  sidebarBranchesKey,
  sidebarBranchHeightsKey,
  sidebarClientCountriesKey,
  sidebarClientsKey,
  sidebarTicketCountriesKey,
  teamAddressesKey,
  viewStateKey,
  type StorageKey,
  type TypedStorage,
} from "../storage/local.ts";
import { movedRoute } from "./address.ts";

/**
 * What this device keeps under a Team's address follows the Team when the address changes (001
 * FR-157, FR-158, 2026-09-29): the old address redirects nowhere and is released to any Team once
 * its hold ends, so a remembered route, a view's state or the last Team opened that still named
 * it would lead to a Team that is not this one, or to none. Each kept value names the Team in one of three places: the whole scope,
 * the first part of the scope, or the part after a tag such as `ws:`; and a route kept as a value
 * names it as the route's first segment. The storage test holds every key of `storage/local.ts`
 * to one of these or to naming no Team at all.
 */

/** A scope's form under the new address, or null when the scope is not the Team's. */
type ScopeMove = (scope: string) => string | null;

function moveScopes<T>(storage: TypedStorage, key: StorageKey<T>, move: ScopeMove, rewrite: (value: T) => T = (value) => value): void {
  for (const scope of storage.scopesOf(key)) {
    const next = move(scope);
    if (next === null) continue;
    const value = storage.get(key, scope);
    storage.remove(key, scope);
    if (value !== null) storage.set(key, rewrite(value), next);
  }
}

/** The whole scope is the address. */
const whole = (from: string, to: string): ScopeMove => (scope) => (scope === from ? to : null);

/** The scope's first part is the address, before a `:`. */
const leading = (from: string, to: string): ScopeMove => (scope) => (scope.startsWith(`${from}:`) ? `${to}${scope.slice(from.length)}` : null);

/** The address follows one of `tags` and a `:`, alone or before further parts. */
const tagged =
  (tags: readonly string[], from: string, to: string): ScopeMove =>
  (scope) => {
    for (const tag of tags) {
      const prefix = `${tag}:${from}`;
      if (scope === prefix || scope.startsWith(`${prefix}:`)) return `${tag}:${to}${scope.slice(prefix.length)}`;
    }
    return null;
  };

/**
 * Move everything kept under the Team's address `from` to `to`, rewrite the routes kept as values
 * that start with it, and remember `to` as the Team's address. Moving a Team with nothing kept
 * under `from` changes nothing but the remembered address.
 */
export function followTeamAddress(storage: TypedStorage, workspaceId: string, from: string, to: string): void {
  const route = (value: string) => movedRoute(value, from, to);
  moveScopes(storage, lastRouteKey, whole(from, to), route);
  moveScopes(storage, modeRouteKey, whole(from, to), (routes) => Object.fromEntries(Object.entries(routes).map(([mode, value]) => [mode, route(value)])));
  moveScopes(storage, recentItemsKey, leading(from, to), (items) => items.map((item) => ({ ...item, route: route(item.route) })));
  moveScopes(storage, lastCreateProjectKey, whole(from, to));
  moveScopes(storage, accountListColumnsKey, whole(from, to));
  moveScopes(storage, sidebarBranchHeightsKey, whole(from, to));
  for (const key of [sidebarBranchesKey, sidebarClientsKey, sidebarClientCountriesKey, sidebarTicketCountriesKey, clientListHeadingsKey]) moveScopes(storage, key, whole(from, to));
  moveScopes(storage, clientHubSectionsKey, leading(from, to));
  moveScopes(storage, inboxFilterKey, tagged(["ws"], from, to));
  moveScopes(storage, viewStateKey, tagged(["ws", "clients", "meetings"], from, to));
  if (storage.get(lastWorkspaceKey) === from) storage.set(lastWorkspaceKey, to);
  // The assistant panel's thread of each Team is held by the Team's address (019, 2026-10-14).
  const threads = storage.get(assistantThreadKey);
  const thread = threads?.[from];
  if (threads !== null && thread !== undefined) storage.set(assistantThreadKey, { ...Object.fromEntries(Object.entries(threads).filter(([slug]) => slug !== from)), [to]: thread });
  const onboarding = storage.get(onboardingProgressKey);
  if (onboarding?.workspace?.slug === from) storage.set(onboardingProgressKey, { ...onboarding, workspace: { ...onboarding.workspace, slug: to } });
  storage.set(teamAddressesKey, { ...(storage.get(teamAddressesKey) ?? {}), [workspaceId]: to });
}

/** One Team whose address changed since this device last saw it. */
export interface TeamMove {
  readonly workspaceId: string;
  readonly from: string;
  readonly to: string;
}

/**
 * Compare the caller's Teams, as `GET /me` lists them, with the addresses this device last saw,
 * follow every Team whose address changed, and remember the current ones. A Team the caller no
 * longer belongs to is forgotten. Answers the Teams it followed.
 */
export function rememberTeamAddresses(storage: TypedStorage, memberships: readonly { readonly workspace_id: string; readonly slug: string }[]): TeamMove[] {
  const known = storage.get(teamAddressesKey) ?? {};
  const moves: TeamMove[] = [];
  for (const membership of memberships) {
    const seen = known[membership.workspace_id];
    if (seen !== undefined && seen !== membership.slug) {
      followTeamAddress(storage, membership.workspace_id, seen, membership.slug);
      moves.push({ workspaceId: membership.workspace_id, from: seen, to: membership.slug });
    }
  }
  storage.set(teamAddressesKey, Object.fromEntries(memberships.map((membership) => [membership.workspace_id, membership.slug])));
  return moves;
}
